import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Building2, UserPlus, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity, OPPORTUNITY_STATUSES } from '@/hooks/useOpportunities';
import { ContactModal } from '@/components/crm/ContactModal';
import { Separator } from '@/components/ui/separator';

interface Organization {
  id: string;
  name: string;
  website: string | null;
  favicon_url: string | null;
  logo_url: string | null;
}

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  org_id: string | null;
}

interface OpportunityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity?: Opportunity | null;
  onSave: (data: Partial<Opportunity> & { contact_ids?: string[] }) => Promise<void>;
}

export function OpportunityModal({ open, onOpenChange, opportunity, onSave }: OpportunityModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    org_id: '',
    status: 'prospect',
    total_fleet: '',
    electric_trucks: '',
    description: '',
    tags: [] as string[],
  });
  const [newTag, setNewTag] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [saving, setSaving] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOrganizations();
      fetchContacts();
      
      if (opportunity) {
        setFormData({
          name: opportunity.name,
          org_id: opportunity.org_id || '',
          status: opportunity.status,
          total_fleet: opportunity.total_fleet?.toString() || '',
          electric_trucks: opportunity.electric_trucks?.toString() || '',
          description: opportunity.description || '',
          tags: opportunity.tags || [],
        });
        // Load existing contacts
        const existingContactIds = opportunity.opportunity_contacts?.length
          ? opportunity.opportunity_contacts
              .sort((a, b) => a.position - b.position)
              .map(oc => oc.contact_id)
          : opportunity.contact_id ? [opportunity.contact_id] : [];
        setSelectedContactIds(existingContactIds);
      } else {
        setFormData({
          name: '',
          org_id: '',
          status: 'prospect',
          total_fleet: '',
          electric_trucks: '',
          description: '',
          tags: [],
        });
        setSelectedContactIds([]);
      }
      setNewTag('');
    }
  }, [open, opportunity]);

  const fetchOrganizations = async () => {
    const { data } = await supabase
      .from('organizations')
      .select('id, name, website, favicon_url, logo_url')
      .is('deleted_at', null)
      .order('name');
    setOrganizations(data || []);
  };

  const fetchContacts = async () => {
    const { data } = await supabase
      .from('contacts')
      .select('id, first_name, last_name, email, org_id')
      .is('deleted_at', null)
      .order('first_name');
    setContacts(data || []);
  };

  const getOrgIcon = (org: Organization | null | undefined) => {
    if (!org) return <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
    
    if (org.logo_url || org.favicon_url) {
      return (
        <img
          src={org.logo_url || org.favicon_url || ''}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    if (org.website) {
      const domain = org.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return (
        <img
          src={`https://www.google.com/s2/favicons?sz=32&domain=${domain}`}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    return <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
  };

  const getContactOrg = (contact: Contact) => {
    return organizations.find(o => o.id === contact.org_id);
  };

  // Filter contacts by selected organization (if any)
  const filteredContacts = formData.org_id && formData.org_id !== '__none__'
    ? contacts.filter(c => c.org_id === formData.org_id)
    : contacts;

  // Get available contacts (not yet selected)
  const availableContacts = filteredContacts.filter(c => !selectedContactIds.includes(c.id));

  const handleAddContact = (contactId: string) => {
    if (contactId === '__new__') {
      setIsContactModalOpen(true);
      return;
    }
    if (contactId && contactId !== '__none__') {
      setSelectedContactIds(prev => [...prev, contactId]);
    }
  };

  const handleContactCreated = async () => {
    await fetchContacts();
  };

  const handleRemoveContact = (index: number) => {
    setSelectedContactIds(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    const trimmed = newTag.trim().toUpperCase();
    if (!trimmed || formData.tags.includes(trimmed)) {
      setNewTag('');
      return;
    }
    setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      await onSave({
        name: formData.name,
        org_id: formData.org_id && formData.org_id !== '__none__' ? formData.org_id : null,
        status: formData.status,
        total_fleet: formData.total_fleet ? parseInt(formData.total_fleet) : null,
        electric_trucks: formData.electric_trucks ? parseInt(formData.electric_trucks) : null,
        description: formData.description || null,
        tags: formData.tags,
        contact_ids: selectedContactIds,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const selectedOrg = organizations.find(o => o.id === formData.org_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{opportunity ? 'Opportunity bewerken' : 'Nieuwe Opportunity'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Naam *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Opportunity naam"
            />
          </div>

          <div>
            <Label>Organisatie</Label>
            <Select 
              value={formData.org_id || '__none__'} 
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, org_id: value === '__none__' ? '' : value }));
                // Reset contacts when org changes
                setSelectedContactIds([]);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer organisatie">
                  {selectedOrg ? (
                    <div className="flex items-center gap-2">
                      {getOrgIcon(selectedOrg)}
                      <span>{selectedOrg.name}</span>
                    </div>
                  ) : formData.org_id && formData.org_id !== '__none__' ? null : 'Selecteer organisatie'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Geen organisatie</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    <div className="flex items-center gap-2">
                      {getOrgIcon(org)}
                      <span>{org.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dynamic Contact Persons */}
          <div className="space-y-2">
            <Label>Contactpersonen</Label>
            
            {/* Selected contacts */}
            {selectedContactIds.map((contactId, index) => {
              const contact = contacts.find(c => c.id === contactId);
              if (!contact) return null;
              const contactOrg = getContactOrg(contact);
              
              return (
                <div key={contactId} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                  {getOrgIcon(contactOrg)}
                  <span className="flex-1 text-sm">
                    {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleRemoveContact(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}

            {/* Add new contact selector - always show */}
            <Select 
              value="__none__"
              onValueChange={handleAddContact}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Contactpersoon ${selectedContactIds.length + 1} toevoegen...`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__new__">
                  <div className="flex items-center gap-2 text-primary">
                    <UserPlus className="h-4 w-4" />
                    <span>Nieuw contact aanmaken...</span>
                  </div>
                </SelectItem>
                {availableContacts.length > 0 && <Separator className="my-1" />}
                <SelectItem value="__none__">Selecteer contactpersoon</SelectItem>
                {availableContacts.map((contact) => {
                  const contactOrg = getContactOrg(contact);
                  return (
                    <SelectItem key={contact.id} value={contact.id}>
                      <div className="flex items-center gap-2">
                        {getOrgIcon(contactOrg)}
                        <span>{`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Status</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPPORTUNITY_STATUSES.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-2 w-2 rounded-full" 
                        style={{ backgroundColor: status.color }}
                      />
                      <span>{status.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="total_fleet">Totale vloot</Label>
              <Input
                id="total_fleet"
                type="number"
                value={formData.total_fleet}
                onChange={(e) => setFormData(prev => ({ ...prev, total_fleet: e.target.value }))}
                placeholder="Aantal voertuigen"
              />
            </div>
            <div>
              <Label htmlFor="electric_trucks">Aantal e-trucks</Label>
              <Input
                id="electric_trucks"
                type="number"
                value={formData.electric_trucks}
                onChange={(e) => setFormData(prev => ({ ...prev, electric_trucks: e.target.value }))}
                placeholder="Elektrische voertuigen"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag) => (
                <Badge key={tag} variant="default" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                  {tag} <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
            <Input
              placeholder="Nieuwe tag (Enter om toe te voegen)..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              className="uppercase"
              maxLength={20}
            />
          </div>

          <div>
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Beschrijving van de opportunity..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={saving || !formData.name.trim()}>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Contact Modal for creating new contacts */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        contact={formData.org_id && formData.org_id !== '__none__' ? { org_id: formData.org_id } as any : null}
        onSave={handleContactCreated}
      />
    </Dialog>
  );
}
